import React from 'react';
import { Box, Card, Chip, Grid, Typography } from '@mui/material';
import { CollectionClientDetail, CollectionContact, CollectionInvoiceDocument } from '../model';
import CollectionClientContactsCard from './CollectionClientContactsCard';
import CollectionClientBackupEmailCard from './CollectionClientBackupEmailCard';
import {
  AddressValue,
  DetailField,
  SectionLabel,
  formatCityStateZip,
  formatStreetLine,
  hasAnyValue,
} from './clientDetailPresentation';
import { ContactEmailAction } from '../hooks/useToggleCollectionClientContactEmail';

interface Props {
  client: CollectionClientDetail;
  clientId: number;
  readOnly?: boolean;
  recipientEmailsLabel?: string;
  invoiceDocuments?: CollectionInvoiceDocument[];
  recipientEmails?: string[];
  contactsOverride?: CollectionContact[];
  recipientEmailsDisplay?: string[];
  allowBackupInRecipientEmptyState?: boolean;
  onToggleContactEmail?: (conIdNo: string, action: ContactEmailAction) => Promise<void>;
  isTogglingContactEmail?: boolean;
  onEmailSent?: () => void;
  onBackupSaved?: () => void;
}

const ClientCompanyInfoCard: React.FC<Props> = ({
  client,
  clientId,
  readOnly = false,
  recipientEmailsLabel = 'Collection emails sent to:',
  invoiceDocuments = [],
  recipientEmails = [],
  contactsOverride,
  recipientEmailsDisplay,
  allowBackupInRecipientEmptyState = true,
  onToggleContactEmail,
  isTogglingContactEmail = false,
  onEmailSent,
  onBackupSaved,
}) => {
  const billingStreet = formatStreetLine(client.billing_address_1, client.billing_address_2, client.billing_address_3);
  const billingCityLine = formatCityStateZip(client.billing_city, client.billing_state, client.billing_zip);
  const physicalStreet = formatStreetLine(
    client.physical_address_1,
    client.physical_address_2,
    client.physical_address_3,
  );
  const physicalCityLine = formatCityStateZip(client.physical_city, client.physical_state, client.physical_zip);
  const hasPhysicalAddress = hasAnyValue(
    client.physical_address_1,
    client.physical_address_2,
    client.physical_address_3,
    client.physical_city,
    client.physical_state,
    client.physical_zip,
  );
  const contactsForDisplay = contactsOverride ?? client.contacts;
  const companyEmails = recipientEmailsDisplay ?? client.servman_emails;

  return (
    <Card sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SectionLabel>Company Info</SectionLabel>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DetailField label="Company">
              <Typography variant="body2">{client.company}</Typography>
            </DetailField>
          </Grid>
          <Grid item xs={6}>
            <DetailField label="Bill Code">
              <Typography variant="body2">{client.bill_code}</Typography>
            </DetailField>
          </Grid>
        </Grid>

        {client.com_or_res && (
          <DetailField label="Company Type">
            <Typography variant="body2">{client.com_or_res === 'C' ? 'Commercial' : 'Home Services'}</Typography>
          </DetailField>
        )}
        {client.terms_description && (
          <DetailField label="Payment Terms">
            <Typography variant="body2">{client.terms_description}</Typography>
          </DetailField>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DetailField label="Billing Company">
              <Typography variant="body2">{client.billing_company || '—'}</Typography>
            </DetailField>
          </Grid>
          <Grid item xs={6}>
            <DetailField label="Billing Address">
              <AddressValue street={billingStreet} cityLine={billingCityLine} />
            </DetailField>
          </Grid>
        </Grid>

        {hasPhysicalAddress && (
          <DetailField label="Physical Address">
            <AddressValue street={physicalStreet} cityLine={physicalCityLine} />
          </DetailField>
        )}

        <DetailField label="Contacts">
          <CollectionClientContactsCard
            contacts={contactsForDisplay}
            recipientEmails={recipientEmails}
            onToggleContact={onToggleContactEmail ?? (() => Promise.resolve())}
            disabled={isTogglingContactEmail}
            clientId={clientId}
            location={client.location}
            invoiceDocuments={invoiceDocuments}
            onEmailSent={onEmailSent ?? (() => undefined)}
            showActions={!readOnly}
          />
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {recipientEmailsLabel}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
              {companyEmails.length > 0 ? (
                companyEmails.map((email) => <Chip key={email} label={email} size="small" />)
              ) : (
                <Chip
                  label={
                    allowBackupInRecipientEmptyState && client.backup_email
                      ? `Backup: ${client.backup_email}`
                      : 'No client email'
                  }
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </DetailField>

        {readOnly ? (
          allowBackupInRecipientEmptyState ? (
            <DetailField label="Backup Email">
              <Typography variant="body2">{client.backup_email || '—'}</Typography>
            </DetailField>
          ) : null
        ) : (
          <CollectionClientBackupEmailCard client={client} onSaved={onBackupSaved ?? (() => undefined)} />
        )}
      </Box>
    </Card>
  );
};

export default ClientCompanyInfoCard;
